/**
 * Haushaltsschlüssel (E21, 25.10.2026) — Geteiltes Ende-zu-Ende.
 *
 * Jedes Konto mit Kontoschlüssel hat ein Schlüsselpaar: öffentlich beim
 * Server, privat dort nur mit dem Kontoschlüssel verpackt. Den
 * Haushaltsschlüssel legt das erste Gerät an, sobald **alle Erwachsenen** des
 * Haushalts ein Schlüsselpaar haben — vorher verschlüsselt, hieße: die anderen
 * könnten Geteiltes nicht mehr lesen. Jedes Gerät mit Haushaltsschlüssel reicht
 * ihn Mitgliedern nach, denen er fehlt (neu beigetreten, neues Schlüsselpaar).
 *
 * Läuft nach dem Abgleich (`useSync`), nicht öfter als alle fünf Minuten.
 */

import { apiFetch } from '../api.js';
import { currentRole } from '../auth.js';
import { db } from '../db/local.js';
import type { KraftwerkDatabase } from '../db/local.js';
import { markTablesResealed, openHeldRows, resealAll } from '../sync/e2e.js';
import { ENCRYPTED_TABLES } from '@kraftwerk/shared';
import {
  fromB64,
  newAccountKey,
  newIdentity,
  openWithAccountKey,
  randomBytes,
  readAccountKey,
  readHouseholdKey,
  readRawAccountKey,
  readIdentity,
  sealWithAccountKey,
  knownKeys,
  readHouseholdGeneration,
  replaceHouseholdKey,
  storeAccountKey,
  storeIdentity,
  toB64,
  unwrapHouseholdKey,
  wrapHouseholdKey,
} from './e2e.js';

interface ServerKey {
  kind: string;
  wrapped: string;
  publicKey: string | null;
}

interface HouseholdState {
  householdId: string;
  encrypted: boolean;
  /** Geltender Haushaltsschlüssel — nach einem Austritt ein neuer (26.10.2026). */
  generation?: number;
  /** Jemand ist gegangen, noch niemand hat neu verschlüsselt. */
  rotate?: boolean;
  mine: string | null;
  members: { userId: string; role: string; publicKey: string | null; hasKey: boolean }[];
}

const PENDING_META = 'e2eHouseholdsPending';

/** Das eigene Schlüsselpaar: vom Server auspacken oder neu anlegen. */
export async function ensureIdentity(database: KraftwerkDatabase = db): Promise<boolean> {
  if (await readIdentity(database)) return true;
  const accountKey = await readAccountKey(database);
  if (!accountKey) return false;
  const { keys } = await apiFetch<{ keys: ServerKey[] }>('/keys');
  const existing = keys.find((key) => key.kind === 'identity');
  if (existing) {
    try {
      await storeIdentity(await openWithAccountKey(existing.wrapped, accountKey), database);
      return true;
    } catch {
      // Mit einem anderen Kontoschlüssel verpackt — kann nicht sein, aber dann
      // lieber ein neues Paar als gar keins.
    }
  }
  const identity = await newIdentity();
  await apiFetch('/keys/identity', {
    method: 'POST',
    body: JSON.stringify({
      publicKey: identity.publicKey,
      wrapped: await sealWithAccountKey(identity.privatePkcs8, accountKey),
      salt: toB64(randomBytes(16)),
    }),
  });
  await storeIdentity(identity.privatePkcs8, database);
  return true;
}

export interface HouseholdKeyOutcome {
  /** Haushalte, in denen dieses Gerät den Schlüssel hat. */
  ready: number;
  /** Haushalte, die noch auf Erwachsene ohne Verschlüsselung warten. */
  waiting: number;
}

/** Haushaltsschlüssel holen, anlegen oder nachreichen. */
export async function ensureHouseholdKeys(
  database: KraftwerkDatabase = db,
): Promise<HouseholdKeyOutcome> {
  const outcome = { ready: 0, waiting: 0 };
  if (!(await ensureIdentity(database))) return outcome;
  const privateKey = (await readIdentity(database))!;
  const { households } = await apiFetch<{ households: HouseholdState[] }>('/keys/households');

  for (const household of households) {
    const { householdId, members } = household;
    const generation = household.generation ?? 1;
    let raw: Uint8Array<ArrayBuffer> | null = null;

    if (household.mine) {
      if (!(await hasCurrentKey(householdId, generation, database))) {
        raw = await unwrapHouseholdKey(household.mine, privateKey, householdId);
        await replaceHouseholdKey(householdId, raw, generation, database);
        await openHeldRows(database);
        // Eigene Mitess-Zeilen (27.10.2026): Die kann nur ich neu senden —
        // das Gerät, das den Schlüssel anlegte, darf es nicht für mich.
        await resealAll(database, householdId, ['household_meal_eaters']);
      }
    } else if (household.rotate && (await readHouseholdKey(householdId, database))) {
      // Jemand ist gegangen und kennt den alten: neuen anlegen, für die
      // Verbliebenen verpacken, Geteiltes damit neu senden.
      raw = await createHouseholdKey(householdId, generation, members);
      if (!raw) continue;
      await replaceHouseholdKey(householdId, raw, generation, database);
      await resealAll(database, householdId);
    } else if (!household.encrypted) {
      const adults = members.filter((member) => member.role === 'adult');
      if (!adults.every((member) => member.publicKey)) {
        outcome.waiting += 1;
        continue;
      }
      raw = await createHouseholdKey(householdId, generation, members);
      if (!raw) continue;
      await replaceHouseholdKey(householdId, raw, generation, database);
      // Was bisher lesbar beim Server liegt, geht jetzt verschlüsselt neu.
      await resealAll(database, householdId);
    } else {
      // Verschlüsselt, aber für mich noch nicht verpackt: ein anderes Gerät reicht nach.
      outcome.waiting += 1;
      continue;
    }

    outcome.ready += 1;
    const key = raw ?? (await rawHouseholdKey(householdId, database));
    const missing = members.filter((member) => member.publicKey && !member.hasKey);
    if (key && missing.length > 0 && household.encrypted && !household.rotate) {
      const wraps = await Promise.all(
        missing.map(async (member) => ({
          userId: member.userId,
          wrapped: await wrapHouseholdKey(key, member.publicKey!, householdId),
        })),
      );
      await apiFetch('/keys/households', {
        method: 'POST',
        body: JSON.stringify({ householdId, generation, wraps }),
      }).catch(() => undefined);
    }
  }
  await database.meta.put({ key: PENDING_META, value: String(outcome.waiting) });
  return outcome;
}

/**
 * Kinder (26.10.2026): kein Kontoschlüssel, also nur ein Schlüsselpaar, dessen
 * privater Teil allein auf diesem Gerät liegt. Den Haushaltsschlüssel verpackt
 * ein Elterngerät (`ensureHouseholdKeys` reicht allen Mitgliedern mit
 * Schlüsselpaar nach). Passt die Verpackung nicht zu diesem Gerät (ein
 * anderes Gerät des Kindes hat inzwischen ein eigenes Paar gemeldet), meldet
 * es seins neu an.
 */
export async function ensureChildKeys(
  database: KraftwerkDatabase = db,
): Promise<HouseholdKeyOutcome> {
  const outcome = { ready: 0, waiting: 0 };
  let privateKey = await readIdentity(database);
  if (!privateKey) privateKey = await announceChildIdentity(database);
  const { households } = await apiFetch<{ households: HouseholdState[] }>('/keys/households');

  for (const household of households) {
    const { householdId } = household;
    const generation = household.generation ?? 1;
    if (await hasCurrentKey(householdId, generation, database)) {
      outcome.ready += 1;
      continue;
    }
    if (household.mine) {
      try {
        const raw = await unwrapHouseholdKey(household.mine, privateKey, householdId);
        await replaceHouseholdKey(householdId, raw, generation, database);
        await openHeldRows(database);
        outcome.ready += 1;
        continue;
      } catch {
        privateKey = await announceChildIdentity(database);
      }
    }
    if (household.encrypted) outcome.waiting += 1;
  }
  await database.meta.put({ key: PENDING_META, value: String(outcome.waiting) });
  const ready = households.find((household) => household.mine);
  if (ready && (await hasCurrentKey(ready.householdId, ready.generation ?? 1, database))) {
    await ensureChildAccountKey(ready.householdId, ready.generation ?? 1, database);
  }
  return outcome;
}

/**
 * Kontoschlüssel eines Kindes (26.10.2026, Timo: die Eltern verwahren mit).
 * Er entsteht auf dem Gerät des Kindes, sobald es den Haushaltsschlüssel hat,
 * und liegt beim Server nur mit diesem verpackt (`/keys/child-escrow`). Ein
 * neues Gerät des Kindes holt ihn so zurück — geht eins verloren, ist nichts
 * weg. Danach ist Privates des Kindes verschlüsselt wie bei Erwachsenen.
 */
async function ensureChildAccountKey(
  householdId: string,
  generation: number,
  database: KraftwerkDatabase,
): Promise<void> {
  const householdKey = (await readHouseholdKey(householdId, database))!;
  const { keys } = await apiFetch<{ keys: (ServerKey & { salt: string })[] }>('/keys');
  const escrow = keys.find((key) => key.kind === 'escrow');
  const own = await readRawAccountKey(database);

  if (own) {
    // Mit dem Schlüssel eines früheren Haushalts oder einem alten verpackt
    // (Austritt): neu packen, sonst käme ein neues Gerät nicht mehr heran.
    if (escrow && escrowTag(escrow.salt) !== `${householdId}:${generation}`) {
      await postEscrow(householdId, await sealWithAccountKey(own, householdKey), true);
    }
    return;
  }

  if (escrow) {
    // Jeder Haushaltsschlüssel, den das Gerät kennt — auch ein alter.
    for (const key of await knownKeys(database)) {
      try {
        await storeAccountKey(await openWithAccountKey(escrow.wrapped, key), database);
        await openHeldRows(database);
        return;
      } catch {
        // Nächster.
      }
    }
    // Nicht zu öffnen: warten, bis ein Gerät mit dem Schlüssel neu packt —
    // nie einen zweiten anlegen.
    return;
  }

  const raw = newAccountKey();
  const created = await postEscrow(householdId, await sealWithAccountKey(raw, householdKey));
  // Ein anderes Gerät des Kindes war schneller: beim nächsten Mal dessen holen.
  if (!created) return;
  await storeAccountKey(raw, database);
  // Was bisher lesbar beim Server liegt, einmal verschlüsselt neu senden.
  await resealAll(database, null, ENCRYPTED_TABLES, true);
  await markTablesResealed(ENCRYPTED_TABLES, database);
}

/** `Haushalt:Generation` — vor dem Schlüsselwechsel stand nur der Haushalt da. */
function escrowTag(salt: string): string {
  return salt.includes(':') ? salt : `${salt}:1`;
}

async function postEscrow(householdId: string, wrapped: string, replace = false): Promise<boolean> {
  try {
    await apiFetch('/keys/child-escrow', {
      method: 'POST',
      body: JSON.stringify({ householdId, wrapped, ...(replace ? { replace } : {}) }),
    });
    return true;
  } catch {
    return false;
  }
}

async function announceChildIdentity(
  database: KraftwerkDatabase,
): Promise<Uint8Array<ArrayBuffer>> {
  const identity = await newIdentity();
  await apiFetch('/keys/child-identity', {
    method: 'POST',
    body: JSON.stringify({ publicKey: identity.publicKey }),
  });
  await storeIdentity(identity.privatePkcs8, database);
  return identity.privatePkcs8;
}

/** Hat dieses Gerät den geltenden Haushaltsschlüssel (nicht nur einen alten)? */
async function hasCurrentKey(
  householdId: string,
  generation: number,
  database: KraftwerkDatabase,
): Promise<boolean> {
  return (
    Boolean(await readHouseholdKey(householdId, database)) &&
    (await readHouseholdGeneration(householdId, database)) === generation
  );
}

/** Neuer Haushaltsschlüssel, für jedes Mitglied mit Schlüsselpaar verpackt. `null`: ein anderes Gerät war schneller. */
async function createHouseholdKey(
  householdId: string,
  generation: number,
  members: HouseholdState['members'],
): Promise<Uint8Array<ArrayBuffer> | null> {
  const raw = newAccountKey();
  const wraps = await Promise.all(
    members
      .filter((member) => member.publicKey)
      .map(async (member) => ({
        userId: member.userId,
        wrapped: await wrapHouseholdKey(raw, member.publicKey!, householdId),
      })),
  );
  try {
    await apiFetch('/keys/households', {
      method: 'POST',
      body: JSON.stringify({ householdId, generation, wraps }),
    });
    return raw;
  } catch {
    // Beim nächsten Mal liegt meine Verpackung bereit.
    return null;
  }
}

async function rawHouseholdKey(
  householdId: string,
  database: KraftwerkDatabase,
): Promise<Uint8Array<ArrayBuffer> | null> {
  const value = (await database.meta.get(`hk:${householdId}`))?.value;
  return value ? fromB64(value) : null;
}

/** Wie viele Haushalte noch auf Erwachsene ohne Verschlüsselung warten. */
export async function readHouseholdsPending(database: KraftwerkDatabase = db): Promise<number> {
  return Number((await database.meta.get(PENDING_META))?.value ?? 0);
}

let lastRun = 0;
const EVERY_MS = 5 * 60 * 1000;

/** Für `useSync`: höchstens alle fünf Minuten — mit Kontoschlüssel oder als Kind. */
export async function maybeEnsureHouseholdKeys(
  database: KraftwerkDatabase = db,
  now: number = Date.now(),
  child: boolean = currentRole() === 'child',
): Promise<void> {
  if (now - lastRun < EVERY_MS) return;
  if (child) {
    lastRun = now;
    await ensureChildKeys(database);
    return;
  }
  if (!(await readAccountKey(database))) return;
  lastRun = now;
  await ensureHouseholdKeys(database);
}

/** Sofort beim nächsten Mal (nach Einrichten, Entsperren, Beitritt). */
export function resetHouseholdKeyTimer(): void {
  lastRun = 0;
}
